import { ComponentType } from 'react'
import { MapPin, MessageSquare, Smartphone, UserCog } from 'lucide-react'
import { BoxTruckDriverLastLocation, BoxTruckLocationSource } from '@numeo/types-core'
import { TelegramGlyphIcon } from 'components/icons/TelegramGlyphIcon'
import { fmtDateTime, fmtProfileLocation, googleMapsUrl } from '../utils/format'
import { DriverLocationEditor } from './DriverLocationEditor'
import { BoxTruckAvailableAtNote } from './BoxTruckAvailableAtNote'
import { BoxTruckLocationAgeBadge } from './BoxTruckLocationAgeBadge'

/**
 * The server's own last-location shape, not a copy of it.
 *
 * This was a local interface listing the same fields one short, which is how a client contract drifts: the
 * server adds a field, the client keeps compiling, and the cell silently stops rendering it. Adding the
 * availability pair was the second time that list had to be edited in step with the DTO, so it now IS the
 * DTO (CLAUDE.md — a type a service returns and a client consumes belongs in `@numeo/types`).
 */
type LastLocation = BoxTruckDriverLastLocation

interface BoxTruckLastLocationCellProps {
    lastLocation?: LastLocation
    /** Driver profile city/state — a fallback locality shown when no GPS locality (or no fix at all). */
    profileLocation?: { city?: string; state?: string }
    /** Present → the cell grows the operator location editor (chevron → city/ZIP popover). The saved fix is
     *  reported so the parent list patches its row. */
    editable?: { userId: string; onSaved: (location: LastLocation) => void }
    /**
     * `cell` (default) stacks freshness, locality and the fix time for a table row. `inline` puts the same
     * facts on ONE line for a detail header, where the exact fix time moves into the tooltip.
     *
     * A variant rather than a second component: everything that is easy to get wrong here — the profile
     * fallback, the missing-source case, the "never hide an older-but-real fix" rule, the editor wiring — is
     * identical either way, and a copy of it beside this one is how the header quietly stops agreeing with
     * the table it was read from.
     */
    layout?: 'cell' | 'inline'
}

/** How the fix reached us, as an icon next to the freshness badge. A fix stored before this shipped carries no
 *  source and renders nothing, which is why every lookup here is optional. `telegram` means the ZIP came from the
 *  driver's Telegram GROUP, which any member of it can post, not necessarily the driver in person; `sms` means
 *  the driver texted the ZIP back from the number on their own claim, so it is the one report that names a
 *  person. */
const SOURCE_ICONS: Record<BoxTruckLocationSource, { Icon: ComponentType<{ className?: string }>; label: string }> = {
    mobile: { Icon: Smartphone, label: 'Mobile' },
    admin: { Icon: UserCog, label: 'Admin' },
    telegram: { Icon: TelegramGlyphIcon, label: 'Telegram' },
    sms: { Icon: MessageSquare, label: 'SMS' },
}

/** Shared "Last location" cell for the Driver Verifications + My Drivers tables. Always shows the last-known
 *  position (locality + when, with a Google Maps pin link — raw coordinates are not shown), colour-badged by
 *  how long ago the fix was taken — never hides an older-but-real location behind "No recent location". Falls
 *  back to profile locality / "—" when there's no fix. */
export const BoxTruckLastLocationCell = ({ lastLocation, profileLocation, editable, layout = 'cell' }: BoxTruckLastLocationCellProps) => {
    const profile = fmtProfileLocation(profileLocation?.city, profileLocation?.state)
    const editor = editable ? (
        <DriverLocationEditor
            userId={editable.userId}
            onSaved={(loc) =>
                editable.onSaved({
                    lat: loc.lat,
                    lng: loc.lng,
                    fixAt: loc.fixAt,
                    city: loc.city ?? undefined,
                    state: loc.state ?? undefined,
                    source: loc.source,
                })
            }
        />
    ) : null

    const captured = fmtProfileLocation(lastLocation?.city, lastLocation?.state)
    const source = lastLocation?.source ? SOURCE_ICONS[lastLocation.source] : null

    if (layout === 'inline') {
        return (
            <div className="flex items-center gap-1.5 text-xs" title={lastLocation ? fmtDateTime(lastLocation.fixAt) : undefined}>
                {lastLocation && <BoxTruckLocationAgeBadge fixAt={lastLocation.fixAt} />}
                <BoxTruckAvailableAtNote availableAt={lastLocation?.availableAt} availableAtText={lastLocation?.availableAtText} />
                {source && (
                    <span className="inline-flex text-foreground-muted" title={source.label} aria-label={source.label}>
                        <source.Icon className="h-3.5 w-3.5" />
                    </span>
                )}
                {/* Captured locality, else the profile's, else nothing — the cell's precedence exactly, so a
                    header and the row it was opened from never disagree about where a driver is. */}
                <span className="truncate font-medium text-foreground">{captured || profile || '—'}</span>
                {lastLocation && (
                    <a
                        href={googleMapsUrl(lastLocation.lat, lastLocation.lng)}
                        target="_blank"
                        rel="noreferrer"
                        title="Open this GPS point in Google Maps"
                        aria-label="Open this GPS point in Google Maps"
                        className="inline-flex h-5 w-5 shrink-0 items-center justify-center rounded-lg bg-default text-default-foreground transition-colors duration-150 hover:bg-default-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                    >
                        <MapPin size={12} aria-hidden />
                    </a>
                )}
                {editor}
            </div>
        )
    }

    if (!lastLocation) {
        return (
            <div className="text-xs text-foreground-muted">
                <span className="inline-flex items-center gap-1">
                    <span>—</span>
                    {editor}
                </span>
                {profile && <div className="mt-0.5">Profile: {profile}</div>}
            </div>
        )
    }

    return (
        <div>
            <div className="flex items-center gap-1.5">
                <BoxTruckLocationAgeBadge fixAt={lastLocation.fixAt} />
                {source && (
                    <span className="inline-flex text-foreground-muted" title={source.label} aria-label={source.label}>
                        <source.Icon className="h-3.5 w-3.5" />
                    </span>
                )}
                <a
                    href={googleMapsUrl(lastLocation.lat, lastLocation.lng)}
                    target="_blank"
                    rel="noreferrer"
                    title="Open this GPS point in Google Maps"
                    aria-label="Open this GPS point in Google Maps"
                    className="inline-flex h-6 w-6 items-center justify-center rounded-lg bg-default text-default-foreground transition-colors duration-150 hover:bg-default-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent"
                >
                    <MapPin size={13} aria-hidden />
                </a>
            </div>
            {captured ? (
                <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground-muted">
                    {captured}
                    {editor}
                </div>
            ) : profile ? (
                <div className="mt-0.5 flex items-center gap-1 text-xs text-foreground-muted">
                    Profile: {profile}
                    {editor}
                </div>
            ) : (
                editor && <div className="mt-0.5">{editor}</div>
            )}
            {/* Under the captured locality, where the operator is already reading the place rather than the
                clock: this line is about the same pin, one step into the future. */}
            <div className="mt-0.5">
                <BoxTruckAvailableAtNote availableAt={lastLocation.availableAt} availableAtText={lastLocation.availableAtText} />
            </div>
            <div className="mt-0.5 text-xs text-foreground-muted">{fmtDateTime(lastLocation.fixAt)}</div>
        </div>
    )
}
